import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPut } from "../utils/apiClient";

// 目標管理ダッシュボード（RN-153）
// 担当者提供のHTMLプロトタイプ（public/target-dashboard.html）を Shadow DOM にカプセル化して表示する。
//  - CSS はプロトタイプのものをそのまま shadow root 内に閉じ込める（アプリ Tailwind と衝突しない／iframe 不要）。
//  - プロトタイプの描画 JS は document.getElementById / querySelectorAll だけを shadow root 参照に差し替えて実行する
//    （ShadowRoot は両メソッドを持つ。createElement は document のままで可）。
//  - ヘッダーはプロトタイプ内蔵のもの1つに統一（アプリ側ヘッダーは付けない＝二重化を回避）。
//  - データは API（/api/target-dashboard、demo は fixtures）から取得し window.__RN153_DATA__ に注入。
//    取得できない場合はプロトタイプ埋め込みのサンプルデータで描画する。

const PROTOTYPE_URL = "/target-dashboard.html";

function currentTargetYm(): string {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), 1); // 対象月は当月（進行中の月の進捗を見る）
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function TargetDashboardPage() {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const mountedRef = useRef(false);
    // "sample" は API 未接続でプロト埋め込みデータを表示中
    const [status, setStatus] = useState<"loading" | "ready" | "sample" | "error">("loading");

    useEffect(() => {
        if (mountedRef.current) return; // StrictMode の二重実行・再マウント対策
        const host = hostRef.current;
        if (!host || host.shadowRoot) return;
        mountedRef.current = true;

        const ym = currentTargetYm();
        const basis = "ly";

        // プロト（目標入力モーダル）の保存フック：GOALS をサーバ（BigQuery）へ永続化
        (window as any).__RN153_SAVE__ = (goals: any) =>
            apiPut(`/api/target-dashboard/targets`, { ym, goals }).catch((e) =>
                // eslint-disable-next-line no-console
                console.error("[TargetDashboard] save failed", e)
            );

        (async () => {
            try {
                // 1) 契約データ（demo は fixtures にリライト。取れなければ null＝サンプル描画）と
                //    プロトタイプ HTML を並列取得
                const [contract, html] = await Promise.all([
                    apiGet(`/api/target-dashboard?ym=${ym}&basis=${basis}`)
                        .then(async (res) => {
                            const ct = res.headers.get("content-type") || "";
                            if (res.ok && ct.includes("application/json")) {
                                const j = await res.json();
                                if (j && (j.daily || j.month)) return j;
                            }
                            return null;
                        })
                        .catch(() => null),
                    fetch(PROTOTYPE_URL, { cache: "no-store" }).then((r) => r.text()),
                ]);

                // 店舗表示名：API は shop_url を返すので、ブラウザの会社情報（localStorage）から
                // 一致する表示名（companyName / shopName）があれば config.storeName を差し替える
                if (contract?.config) {
                    try {
                        const companies = JSON.parse(localStorage.getItem("companies") || "[]");
                        const su = String(contract.config.storeName || "");
                        const c = Array.isArray(companies)
                            ? companies.find((x: any) => String(x?.shopUrl || "") === su)
                            : null;
                        const name = c && (c.companyName || c.shopName);
                        if (name) contract.config.storeName = name;
                    } catch {
                        /* 名前が引けなければ shop_url のまま */
                    }
                }
                (window as any).__RN153_DATA__ = contract || undefined;

                // 2) プロトタイプ HTML から CSS / body / script を抽出
                const doc = new DOMParser().parseFromString(html, "text/html");
                const css = Array.from(doc.querySelectorAll("style"))
                    .map((s) => s.textContent || "")
                    .join("\n");
                const scripts = Array.from(doc.querySelectorAll("script"))
                    .map((s) => s.textContent || "")
                    .join("\n;\n");
                // body から script を除去（innerHTML 経由の script は実行されないが、見た目のため除去）
                doc.querySelectorAll("script").forEach((s) => s.remove());
                const bodyHtml = doc.body ? doc.body.innerHTML : "";

                // 3) shadow root に CSS + markup を投入
                //    プロトタイプは色テーマを :root のCSS変数で定義しているが、Shadow DOM 内では
                //    :root は <html> を指し shadow 内スタイルから一致しない（→ var(--green) 等が未定義になり
                //    グラフ等の色が消える）。:host に置換して shadow ホストに変数を載せ、内部へ継承させる。
                const shadowCss = css.split(":root").join(":host");
                const shadow = host.attachShadow({ mode: "open" });
                shadow.innerHTML = `<style>${shadowCss}</style>${bodyHtml}`;

                // 4) 描画 JS を shadow root 参照に差し替えて実行
                const transformed = scripts
                    .split("document.getElementById").join("__root.getElementById")
                    .split("document.querySelectorAll").join("__root.querySelectorAll");
                // eslint-disable-next-line no-new-func
                const run = new Function("__root", transformed);
                run(shadow);

                setStatus(contract ? "ready" : "sample");
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[TargetDashboard] mount failed", e);
                setStatus("error");
            }
        })();
    }, []);

    return (
        <div className="w-full">
            {status === "loading" && <div className="text-sm text-slate-400 mb-2">読み込み中…</div>}
            {status === "error" && (
                <div className="text-sm text-red-600 mb-2">画面の読み込みに失敗しました。リロードしてください。</div>
            )}
            {status === "sample" && (
                <div className="text-xs text-amber-600 mb-2">サンプルデータ表示中（API未接続）</div>
            )}
            {/* プロトタイプは Shadow DOM 内に閉じる（CSS はアプリへ漏れない） */}
            <div ref={hostRef} />
        </div>
    );
}
